import { useEffect, useState } from "react";
import api from "@/lib/api";
import { History, ChevronLeft, ChevronRight } from "lucide-react";

const PAGE_SIZE = 25;

export default function AuditTab({ oppId }) {
  const [data, setData] = useState(null); // { items, total, limit, offset }
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.get(`/opportunities/${oppId}/audit`, { params: { limit: PAGE_SIZE, offset: page * PAGE_SIZE } })
      .then((r) => setData(r.data))
      .catch(() => setData({ items: [], total: 0, limit: PAGE_SIZE, offset: 0 }))
      .finally(() => setLoading(false));
  }, [oppId, page]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!data) return <p className="text-sm text-zinc-500">Loading…</p>;

  const total = data.total || 0;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const from = total === 0 ? 0 : page * PAGE_SIZE + 1;
  const to = Math.min(total, (page + 1) * PAGE_SIZE);

  return (
    <div data-testid="audit-tab" className="bg-white border border-zinc-200 rounded-sm max-w-4xl">
      <div className="px-4 py-3 border-b border-zinc-200 flex items-center gap-2">
        <History size={14} strokeWidth={1.5} className="text-zinc-400" />
        <h3 className="font-heading text-sm font-medium tracking-tight">Estimation change log</h3>
        <span data-testid="audit-total-count" className="text-xs text-zinc-400 ml-auto font-mono">{total} entries</span>
      </div>

      {total === 0 ? (
        <p className="px-4 py-8 text-xs text-zinc-400">No changes recorded yet.</p>
      ) : (
        <>
          <div className={`divide-y divide-zinc-100 ${loading ? "opacity-50" : ""}`}>
            {data.items.map((l) => (
              <div key={l.id} data-testid={`audit-entry-${l.id}`} className="px-4 py-3 hover:bg-zinc-50">
                <div className="flex items-center justify-between gap-4">
                  <p className="text-[13px] font-medium">{l.action}</p>
                  <p className="text-[11px] text-zinc-400 font-mono whitespace-nowrap">
                    {new Date(l.timestamp).toLocaleString()} · {l.user}
                  </p>
                </div>
                {l.details && <p className="text-xs text-zinc-500 mt-0.5 font-mono break-words">{l.details}</p>}
              </div>
            ))}
          </div>

          <div className="px-4 py-3 border-t border-zinc-200 flex items-center justify-between">
            <span data-testid="audit-page-range" className="text-[11px] text-zinc-400 font-mono">
              {from}–{to} of {total}
            </span>
            <div className="flex items-center gap-1.5">
              <button
                data-testid="audit-prev-page"
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page === 0 || loading}
                className="flex items-center gap-1 text-xs border border-zinc-300 rounded-sm px-2.5 py-1.5 hover:bg-zinc-50 disabled:opacity-40 disabled:cursor-not-allowed">
                <ChevronLeft size={13} strokeWidth={1.5} /> Prev
              </button>
              <span className="text-[11px] text-zinc-500 font-mono px-1">{page + 1} / {pageCount}</span>
              <button
                data-testid="audit-next-page"
                onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
                disabled={page >= pageCount - 1 || loading}
                className="flex items-center gap-1 text-xs border border-zinc-300 rounded-sm px-2.5 py-1.5 hover:bg-zinc-50 disabled:opacity-40 disabled:cursor-not-allowed">
                Next <ChevronRight size={13} strokeWidth={1.5} />
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
